import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { listTabs, type TabOut } from "@/api/tabs";

type TabsContextValue = {
  tabs: TabOut[];
  isLoading: boolean;
  isError: boolean;
  currentTabId: string | null;
  currentTab: TabOut | null;
  setCurrentTabId: (id: string | null) => void;
  refetch: () => void;
};

const CTX = createContext<TabsContextValue | null>(null);
const LS_KEY = "stms.currentTabId";

export function TabsProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ["tabs"],
    queryFn: listTabs,
    staleTime: 30_000,
  });
  const tabs = q.data?.items ?? [];

  const [currentTabId, setCurrentTabIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LS_KEY);
    } catch {
      return null;
    }
  });

  // Auto-select the first tab once tabs load, and clear the selection if the
  // saved tab no longer exists (e.g. superadmin deleted it).
  useEffect(() => {
    if (tabs.length === 0) return;
    const stillExists = currentTabId && tabs.some((t) => t.id === currentTabId);
    if (!stillExists) {
      setCurrentTabIdState(tabs[0].id);
    }
  }, [tabs, currentTabId]);

  useEffect(() => {
    try {
      if (currentTabId) localStorage.setItem(LS_KEY, currentTabId);
      else localStorage.removeItem(LS_KEY);
    } catch {
      // localStorage may be blocked (private mode) – non-fatal.
    }
  }, [currentTabId]);

  const setCurrentTabId = useCallback((id: string | null) => {
    setCurrentTabIdState(id);
  }, []);

  const currentTab = useMemo(
    () => tabs.find((t) => t.id === currentTabId) ?? null,
    [tabs, currentTabId],
  );

  const value = useMemo<TabsContextValue>(
    () => ({
      tabs,
      isLoading: q.isLoading,
      isError: q.isError,
      currentTabId,
      currentTab,
      setCurrentTabId,
      refetch: () => void q.refetch(),
    }),
    [tabs, q.isLoading, q.isError, currentTabId, currentTab, setCurrentTabId, q],
  );

  return <CTX.Provider value={value}>{children}</CTX.Provider>;
}

export function useTabs(): TabsContextValue {
  const ctx = useContext(CTX);
  if (!ctx) {
    throw new Error("useTabs must be called inside <TabsProvider>");
  }
  return ctx;
}

/** Convenience for pages that require an active tab. */
export function useCurrentTabId(): string {
  const { currentTabId } = useTabs();
  if (!currentTabId) {
    throw new Error(
      "useCurrentTabId called without an active tab. Wrap the page in <RequireTab>.",
    );
  }
  return currentTabId;
}
